import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  FiAward,
  FiBookOpen,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiMail,
  FiPhone,
  FiRefreshCcw,
  FiTag,
  FiUser,
  FiX,
} from 'react-icons/fi'
import { fetchTeacherProfile, updateTeacherProfile } from '../../store/slices/teacherSlice'
import './Profile.css'

const buildStateFromProfile = (profile) => ({
  bio: profile?.bio || '',
  subjects: Array.isArray(profile?.subjects) ? profile.subjects.filter(Boolean) : [],
  subjectDraft: '',
  qualifications: Array.isArray(profile?.qualifications) ? profile.qualifications.filter(Boolean) : [],
  qualificationDraft: '',
  officeHours: {
    start: profile?.officeHours?.start || '',
    end: profile?.officeHours?.end || '',
  },
})

const sanitizePayload = (state) => {
  const normalizeList = (list) => {
    if (!Array.isArray(list)) return []
    const seen = new Set()
    return list
      .map((item) => item.trim())
      .filter((item) => {
        if (!item) return false
        const key = item.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
  }

  const payload = {
    subjects: normalizeList(state.subjects),
    qualifications: normalizeList(state.qualifications),
    officeHours: {
      start: state.officeHours?.start || '',
      end: state.officeHours?.end || '',
    },
  }

  const trimmedBio = (state.bio || '').trim()
  if (trimmedBio) {
    payload.bio = trimmedBio
  }

  return payload
}

const Profile = () => {
  const dispatch = useDispatch()
  const { profile, isLoading, error, lastUpdated } = useSelector((state) => state.teacher)
  const authUser = useSelector((state) => state.auth.user)

  const [formState, setFormState] = useState(buildStateFromProfile(null))
  const [statusMessage, setStatusMessage] = useState('')
  const [statusVariant, setStatusVariant] = useState('success')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!profile && !isLoading) {
      dispatch(fetchTeacherProfile())
    }
  }, [dispatch, profile, isLoading])

  useEffect(() => {
    if (profile) {
      setFormState(buildStateFromProfile(profile))
    }
  }, [profile])

  const initialSnapshot = useMemo(() => (profile ? sanitizePayload(buildStateFromProfile(profile)) : null), [profile])
  const currentSnapshot = useMemo(() => sanitizePayload(formState), [formState])

  const hasChanges = useMemo(() => {
    if (!initialSnapshot) return false
    return JSON.stringify(initialSnapshot) !== JSON.stringify(currentSnapshot)
  }, [initialSnapshot, currentSnapshot])

  const memberSince = useMemo(() => {
    const source = profile?.createdAt || authUser?.createdAt
    if (!source) return null
    const parsed = new Date(source)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }, [profile, authUser])

  const lastSyncedLabel = useMemo(() => {
    if (!lastUpdated) return 'Not synced yet'
    const parsed = new Date(lastUpdated)
    if (Number.isNaN(parsed.getTime())) return 'Not synced yet'
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }, [lastUpdated])

  const handleBioChange = (event) => {
    const { value } = event.target
    setFormState((prev) => ({ ...prev, bio: value }))
  }

  const handleOfficeHourChange = (key) => (event) => {
    const { value } = event.target
    setFormState((prev) => ({
      ...prev,
      officeHours: {
        ...prev.officeHours,
        [key]: value,
      },
    }))
  }

  const handleDraftChange = (field) => (event) => {
    const { value } = event.target
    setFormState((prev) => ({ ...prev, [field]: value }))
  }

  const commitChip = (collectionKey, draftKey) => {
    setFormState((prev) => {
      const raw = prev[draftKey].trim()
      if (!raw) {
        return prev
      }

      const exists = prev[collectionKey].some((item) => item.toLowerCase() === raw.toLowerCase())
      if (exists) {
        return { ...prev, [draftKey]: '' }
      }

      return {
        ...prev,
        [collectionKey]: [...prev[collectionKey], raw],
        [draftKey]: '',
      }
    })
  }

  const removeChip = (collectionKey, index) => {
    setFormState((prev) => ({
      ...prev,
      [collectionKey]: prev[collectionKey].filter((_, itemIndex) => itemIndex !== index),
    }))
  }

  const handleDraftKeyDown = (collectionKey, draftKey) => (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commitChip(collectionKey, draftKey)
    }
  }

  const handleDraftBlur = (collectionKey, draftKey) => () => {
    commitChip(collectionKey, draftKey)
  }

  const handleRefresh = useCallback(() => {
    if (isLoading) return
    dispatch(fetchTeacherProfile())
  }, [dispatch, isLoading])

  const handleReset = () => {
    if (profile) {
      setFormState(buildStateFromProfile(profile))
      setStatusMessage('Changes cleared.')
      setStatusVariant('info')
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!profile?._id || isSaving) return

    const payload = sanitizePayload(formState)

    if (payload.bio && payload.bio.length < 5) {
      setStatusMessage('Bio should be at least 5 characters long.')
      setStatusVariant('error')
      return
    }

    setIsSaving(true)
    setStatusMessage('')

    try {
      await dispatch(updateTeacherProfile({ teacherId: profile._id, updates: payload })).unwrap()
      setStatusMessage('Profile updated successfully.')
      setStatusVariant('success')
    } catch (err) {
      const message = err?.message || 'Failed to update profile.'
      setStatusMessage(message)
      setStatusVariant('error')
    } finally {
      setIsSaving(false)
    }
  }

  const summaryCards = useMemo(() => {
    const subjectsCount = currentSnapshot.subjects?.length || 0
    const qualificationsCount = currentSnapshot.qualifications?.length || 0
    const hasOfficeHours = currentSnapshot.officeHours?.start || currentSnapshot.officeHours?.end
    const officeHoursLabel = hasOfficeHours
      ? `${currentSnapshot.officeHours.start || '--:--'} - ${currentSnapshot.officeHours.end || '--:--'}`
      : 'Not configured'

    return [
      {
        label: 'Focus areas',
        value: subjectsCount,
        icon: FiBookOpen,
        description: subjectsCount === 1 ? 'Active subject' : 'Active subjects',
      },
      {
        label: 'Qualifications',
        value: qualificationsCount,
        icon: FiAward,
        description: qualificationsCount === 1 ? 'Credential' : 'Credentials',
      },
      {
        label: 'Office hours',
        value: officeHoursLabel,
        icon: FiClock,
        description: 'Availability window for students',
        emphasize: true,
      },
    ]
  }, [currentSnapshot])

  return (
    <div className="profile-page">
      <header className="profile-hero">
        <div>
          <p className="eyebrow">Account center</p>
          <h1>My Profile</h1>
          <p className="subhead">Manage your profile and account settings</p>
          <div className="profile-meta">
            <span className="profile-meta__badge">
              <FiUser size={14} /> {authUser?.role ? authUser.role.replace(/\b\w/g, (char) => char.toUpperCase()) : 'Staff member'}
            </span>
            {memberSince && (
              <span className="profile-meta__item">
                <FiCalendar size={14} /> Joined {memberSince}
              </span>
            )}
            <span className="profile-meta__item">
              <FiRefreshCcw size={14} /> Last synced · {lastSyncedLabel}
            </span>
          </div>
        </div>
        <button type="button" className="profile-refresh" onClick={handleRefresh} disabled={isLoading}>
          <FiRefreshCcw size={16} /> {isLoading ? 'Refreshing…' : 'Refresh profile'}
        </button>
      </header>

      {statusMessage && (
        <div className={`profile-alert profile-alert--${statusVariant}`}>
          {statusMessage}
        </div>
      )}

      {error && !profile && (
        <div className="profile-state" role="alert">
          Unable to load profile: {error}
        </div>
      )}

      {isLoading && !profile && <div className="profile-state">Loading profile…</div>}

      {profile && (
        <>
          <section className="profile-summary">
            {summaryCards.map((card) => {
              const Icon = card.icon
              return (
                <article key={card.label} className={`profile-summary__card ${card.emphasize ? 'is-highlighted' : ''}`}>
                  <div className="profile-summary__icon">
                    <Icon size={18} />
                  </div>
                  <div>
                    <p>{card.label}</p>
                    <strong>{card.value}</strong>
                    <span>{card.description}</span>
                  </div>
                </article>
              )
            })}
          </section>

          <section className="profile-content">
            <article className="profile-card profile-card--info">
              <header>
                <h2>Account snapshot</h2>
                <p>Key identifiers pulled from your staff login.</p>
              </header>
              <dl className="profile-info-list">
                <div>
                  <dt>Full name</dt>
                  <dd>{profile.user?.firstName || profile.user?.lastName ? `${profile.user?.firstName || ''} ${profile.user?.lastName || ''}`.trim() : profile.fullName || 'Name unavailable'}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    <FiMail size={14} /> {profile.user?.email || 'Not provided'}
                  </dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>
                    <FiPhone size={14} /> {profile.user?.phone || 'Not provided'}
                  </dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span className={`status-pill status-${(profile.status || 'active').toLowerCase()}`}>
                      <FiCheckCircle size={14} /> {(profile.status || 'active').replace(/\b\w/g, (char) => char.toUpperCase())}
                    </span>
                  </dd>
                </div>
              </dl>

              <div className="profile-chips">
                <h3>Active subjects</h3>
                <div className="chip-row">
                  {formState.subjects.length === 0 && <span className="chip chip--empty">No subjects declared</span>}
                  {formState.subjects.map((subject) => (
                    <span key={subject} className="chip">
                      <FiTag size={12} /> {subject}
                    </span>
                  ))}
                </div>
                <h3>Qualifications</h3>
                <div className="chip-row">
                  {formState.qualifications.length === 0 && <span className="chip chip--empty">No qualifications listed</span>}
                  {formState.qualifications.map((qualification) => (
                    <span key={qualification} className="chip">
                      <FiAward size={12} /> {qualification}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            <form className="profile-card profile-card--form" onSubmit={handleSubmit}>
              <header>
                <h2>Profile details</h2>
                <p>Update the information students and admins see across batches and lectures.</p>
              </header>

              <div className="form-field">
                <label htmlFor="profile-bio">Short bio</label>
                <textarea
                  id="profile-bio"
                  name="bio"
                  placeholder="Share a short introduction, expertise, or teaching philosophy."
                  value={formState.bio}
                  onChange={handleBioChange}
                  rows={5}
                />
                <small>Minimum 5 characters. This appears on your batch and lecture touchpoints.</small>
              </div>

              <div className="form-columns">
                <div className="form-field">
                  <label htmlFor="profile-subjects">Focus areas</label>
                  <div className="chip-input">
                    <input
                      id="profile-subjects"
                      type="text"
                      placeholder="Add a subject and press Enter"
                      value={formState.subjectDraft}
                      onChange={handleDraftChange('subjectDraft')}
                      onKeyDown={handleDraftKeyDown('subjects', 'subjectDraft')}
                      onBlur={handleDraftBlur('subjects', 'subjectDraft')}
                    />
                    <button type="button" onClick={() => commitChip('subjects', 'subjectDraft')} aria-label="Add subject">
                      Add
                    </button>
                  </div>
                  {formState.subjects.length > 0 && (
                    <ul className="chip-list">
                      {formState.subjects.map((subject, index) => (
                        <li key={subject}>
                          <span className="chip chip--editable">
                            <FiTag size={12} /> {subject}
                            <button
                              type="button"
                              aria-label={`Remove subject ${subject}`}
                              onClick={() => removeChip('subjects', index)}
                            >
                              <FiX size={12} />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <small>Use Enter or comma to capture each subject.</small>
                </div>

                <div className="form-field">
                  <label htmlFor="profile-qualifications">Certifications &amp; awards</label>
                  <div className="chip-input">
                    <input
                      id="profile-qualifications"
                      type="text"
                      placeholder="Add a qualification and press Enter"
                      value={formState.qualificationDraft}
                      onChange={handleDraftChange('qualificationDraft')}
                      onKeyDown={handleDraftKeyDown('qualifications', 'qualificationDraft')}
                      onBlur={handleDraftBlur('qualifications', 'qualificationDraft')}
                    />
                    <button type="button" onClick={() => commitChip('qualifications', 'qualificationDraft')} aria-label="Add qualification">
                      Add
                    </button>
                  </div>
                  {formState.qualifications.length > 0 && (
                    <ul className="chip-list">
                      {formState.qualifications.map((qualification, index) => (
                        <li key={qualification}>
                          <span className="chip chip--editable">
                            <FiAward size={12} /> {qualification}
                            <button
                              type="button"
                              aria-label={`Remove qualification ${qualification}`}
                              onClick={() => removeChip('qualifications', index)}
                            >
                              <FiX size={12} />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <small>Highlight degrees, certifications, or notable achievements.</small>
                </div>
              </div>

              <div className="form-columns">
                <div className="form-field">
                  <label htmlFor="office-start">Office hours start</label>
                  <input
                    id="office-start"
                    type="time"
                    value={formState.officeHours.start}
                    onChange={handleOfficeHourChange('start')}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="office-end">Office hours end</label>
                  <input
                    id="office-end"
                    type="time"
                    value={formState.officeHours.end}
                    onChange={handleOfficeHourChange('end')}
                  />
                </div>
              </div>

              <div className="profile-actions">
                <button
                  type="button"
                  className="profile-button profile-button--ghost"
                  onClick={handleReset}
                  disabled={!hasChanges || isSaving}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="profile-button profile-button--primary"
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </section>
        </>
      )}
    </div>
  )
}

export default Profile
